import constants from '../../../constants';

const API_BASE = constants.baseApiHost;
const API_KEY = constants.apikey;

// Get session from localStorage
const getSession = () => {
  const localStorageSession = localStorage['session'];
  return localStorageSession && localStorageSession !== 'undefined'
    ? JSON.parse(localStorageSession)
    : null;
};

// Common API headers
const getHeaders = (): Record<string, string> => {
  const session = getSession();
  return {
    ApiKey: API_KEY || '',
    'Content-Type': 'application/json',
    Authorization: session?.key ? `bearer ${session.key}` : ''
  };
};

// Common API error handling
const handleApiResponse = async (response: Response) => {
  if (!response.ok) {
    throw new Error(`API Error: ${response.status} ${response.statusText}`);
  }
  return response.json();
};

/**
 * Fetch daily snapshot data for the home page
 * @param dateFrom - Start date in YYYY-MM-DD format
 * @param bracket - Data bracket (defaultOnline for SysAdmin, default for others)
 * @param dateTo - End date in YYYY-MM-DD format (optional)
 * @returns Promise with daily snapshot data
 */
export const fetchDailySnapshots = async (
  dateFrom: string,
  bracket: string,
  dateTo?: string
) => {
  let url = `${API_BASE}/api/v2/report/snapshot/daily?dateFrom=${dateFrom}&bracket=${bracket}`;
  if (dateTo) {
    url += `&dateTo=${dateTo}`;
  }

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders()
  });

  return handleApiResponse(response);
};

/**
 * Fetch campaign snapshot data for top campaigns widget
 * @param dateFrom - Start date in YYYY-MM-DD format
 * @param bracket - Data bracket (defaultOnline for SysAdmin, default for others)
 * @returns Promise with campaign snapshot data
 */
export const fetchCampaignSnapshots = async (
  dateFrom: string,
  bracket: string
) => {
  const url = `${API_BASE}/api/v2/report/snapshot/campaign?dateFrom=${dateFrom}&bracket=${bracket}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders()
  });

  return handleApiResponse(response);
};

/**
 * Fetch organization snapshot data for top organizations widget (SysAdmin only)
 * @param dateFrom - Start date in YYYY-MM-DD format
 * @param bracket - Data bracket (defaultOnline for SysAdmin, default for others)
 * @returns Promise with organization snapshot data
 */
export const fetchOrganizationSnapshots = async (
  dateFrom: string,
  bracket: string
) => {
  const url = `${API_BASE}/api/v2/report/snapshot/organization?dateFrom=${dateFrom}&bracket=${bracket}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders()
  });

  return handleApiResponse(response);
};

/**
 * Fetch event snapshot data for top events widget (SysAdmin only)
 * @param dateFrom - Start date in YYYY-MM-DD format
 * @param bracket - Data bracket (defaultOnline for SysAdmin, default for others)
 * @returns Promise with event snapshot data
 */
export const fetchEventSnapshots = async (
  dateFrom: string,
  bracket: string
) => {
  const url = `${API_BASE}/api/v2/report/snapshot/event?dateFrom=${dateFrom}&bracket=${bracket}`;

  const response = await fetch(url, {
    method: 'GET',
    headers: getHeaders()
  });

  return handleApiResponse(response);
};

/**
 * Fetch charity place applications (event ambassador applications)
 * @returns Promise with charity place applications data
 */
export const fetchCharityPlaceApplications = async () => {
  const url = `${API_BASE}/api/v2/EventAmbassador/search`;

  const body = {
    page: 1,
    pageSize: 5,
    text: '',
    resultType: 'Full',
    filters: [
      {
        key: 'campaign.status',
        operator: 'EqualTo',
        value: 'live'
      }
    ],
    baseObjectsOnly: true,
    orderBy: {
      key: 'createdAtLocal',
      direction: 'desc'
    }
  };

  const response = await fetch(url, {
    method: 'POST',
    headers: getHeaders(),
    body: JSON.stringify(body)
  });

  return handleApiResponse(response);
};
